import { useTranslation } from 'react-i18next'
import { LottoCardProps } from './LottoCard.types'
import { useThemeAssets } from '../../hooks/useThemeAssets'
import { useThemeFeatures } from '../../hooks/useThemeFeatures'
import { useCurrentEvent, EventType } from '../../hooks/useCurrentEvent'
import { ThemeName } from '../../constants/theme'
import LottoCardFlagHeader from '../LottoCardFlagHeader'
import LottoResultsDivider from '../LottoResultsDivider'

const LottoCard: React.FC<LottoCardProps> = ({
  country,
  title,
  date,
  time,
  sections,
  noRound,
}) => {
  const { t } = useTranslation()
  const { smallFlagImageUrls, svgAssets, themeName } = useThemeAssets()
  const themeFeatures = useThemeFeatures()
  const event = useCurrentEvent()

  // Check if Chinese New Year event is active
  const isChineseNewYear = event === EventType.CHINESE_NEW_YEAR
  // Chinese knot is available from svgAssets when event is active (blue theme only)
  const chineseKnotImage = (svgAssets as { CHINESE_KNOT?: string | null })
    ?.CHINESE_KNOT

  const renderResults = () => {
    return (
      <div
        className={`flex ${sections.length === 1 ? 'justify-center' : 'justify-between'} text-[var(--lotto-card-text)]`}
      >
        {sections.map((section, idx) => (
          <div
            className={`flex flex-col items-${sections.length === 1 ? 'center' : 'start'} gap-4 text-center`}
            key={`${idx}-${section.title}`}
          >
            <h5 className="text-[var(--lotto-card-section-title)]">
              {section.title}
            </h5>
            <h3 className="flex gap-2 justify-center">
              {section.numbers.map((num, numIdx) => (
                <div
                  key={numIdx}
                  className={`${typeof num === 'string' ? '' : 'result-number result-number-style'}`}
                >
                  {typeof num === 'string' ? (
                    <span
                      className={`text-xs text-[var(--nocard-subtext)] md:text-lg font-normal ${
                        themeFeatures.noResultLabelStyled
                          ? 'no-result-label'
                          : ''
                      }`}
                    >
                      {num}
                    </span>
                  ) : (
                    num
                  )}
                </div>
              ))}
            </h3>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div
      className={`lotto-card-style rounded-xl p-8 w-full max-w-md h-[280px] text-center relative ${noRound ? 'lotto-card-no-round' : ''}`}
    >
      {/* Chinese New Year Chinese Knot - Only shows during CNY (Blue theme only) */}
      {isChineseNewYear && chineseKnotImage && themeName === ThemeName.BLUE && (
        <>
          <img
            src={chineseKnotImage}
            alt="Chinese New Year Knot"
            className="cny-lotto-card-knot"
          />
          <img
            src={chineseKnotImage}
            alt="Chinese New Year Knot Small"
            className="cny-lotto-card-knot-small"
          />
        </>
      )}
      {/* Header */}
      <LottoCardFlagHeader
        title={title}
        country={country}
        imageMap={smallFlagImageUrls}
      />

      {!noRound && (
        <div className="flex justify-between text-sm text-[var(--lotto-card-date-text)] mb-2">
          <span>{date}</span>
          <span>{time}</span>
        </div>
      )}

      {/* Divider */}
      <LottoResultsDivider label={t('lottoCard.results')} />

      {/* Results */}
      {renderResults()}
    </div>
  )
}

export default LottoCard
